import api from './api';

// Types for project metadata
export interface ProjectMetadata {
  project_code: string;
  project_name: string;
  developer?: string | null;
  description?: string;
  location?: string;
  plus_code?: string;
  lat?: number;
  lng?: number;
  price_range?: string;
  bedrooms?: string;
  bathrooms?: string;
  area?: string;
  currency?: string;
  link?: string;
  pdf_link?: string;
  floor_plan?: string;
  contact?: string;
  hubspot_virtual_meeting_slug?: string;
  hubspot_virtual_scheduling_enabled?: boolean;
  virtual_advisor_timezone?: string;
  status?: string;
  availability_status?: string;
  is_active?: boolean;
  created_at?: string;
  updated_at?: string;
  created_by?: string;
}

export interface ProjectMetadataCreate {
  project_code: string;
  project_name: string;
  developer?: string | null;
  description?: string;
  location?: string;
  price_range?: string;
  bedrooms?: string;
  bathrooms?: string;
  area?: string;
  currency?: string;
  link?: string;
  pdf_link?: string;
  floor_plan?: string;
  contact?: string;
  hubspot_virtual_meeting_slug?: string;
  hubspot_virtual_scheduling_enabled?: boolean;
  virtual_advisor_timezone?: string;
  status?: string;
}

export interface ProjectMetadataUpdate {
  project_name?: string;
  developer?: string | null;
  description?: string;
  location?: string;
  plus_code?: string | null;
  lat?: number | null;
  lng?: number | null;
  price_range?: string;
  bedrooms?: string;
  bathrooms?: string;
  area?: string;
  currency?: string;
  link?: string | null;
  pdf_link?: string | null;
  floor_plan?: string;
  contact?: string | null;
  hubspot_virtual_meeting_slug?: string | null;
  hubspot_virtual_scheduling_enabled?: boolean;
  virtual_advisor_timezone?: string | null;
  status?: string;
  is_active?: boolean;
}

export interface ProjectAddressPreview {
  project_code: string;
  plus_code?: string | null;
  location?: string | null;
  lat?: number | null;
  lng?: number | null;
}

export interface ProjectPdfUploadResponse {
  project_code: string;
  pdf_link: string;
  document_id: number;
  document_file_name: string;
  updated_at?: string | null;
}

// Types for project context management
export interface ProjectContext {
  id: number;
  project_code: string;
  context_type: string;
  title?: string;
  content: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by?: string;
  documents: ProjectDocument[];
}

export interface ProjectDocument {
  id: number;
  project_context_id?: number;
  project_code: string;
  document_type: string;
  file_name: string;
  original_file_name: string;
  file_path: string;
  file_size: number;
  mime_type: string;
  description?: string;
  is_active: boolean;
  uploaded_at: string;
  updated_at?: string;
  uploaded_by: string;
}

export interface ProjectSummary {
  project_code: string;
  context_count: number;
  document_count: number;
  last_updated?: string;
}

export interface ProjectContextFilters {
  project_code?: string;
  context_type?: string;
  is_active?: boolean;
  created_by?: string;
  search_query?: string;
}

export interface ProjectDocumentFilters {
  project_code?: string;
  project_context_id?: number;
  document_type?: string;
  is_active?: boolean;
  uploaded_by?: string;
}

export interface CreateProjectContextData {
  project_code: string;
  context_type: string;
  title?: string;
  content: string;
  is_active?: boolean;
  created_by: string;
}

export interface UpdateProjectContextData {
  context_type?: string;
  title?: string;
  content?: string;
  is_active?: boolean;
  updated_by: string;
}

export interface UpdateProjectDocumentData {
  document_type?: string;
  description?: string;
  is_active?: boolean;
}

export interface VectorStoreSyncStatus {
  id: number;
  project_code: string;
  sync_type: string;
  status: string;
  started_at: string;
  completed_at?: string;
  error_message?: string;
  items_processed: number;
  items_total: number;
  initiated_by: string;
}

// Context types enum
export const CONTEXT_TYPES = {
  DESCRIPTION: 'description',
  AMENITIES: 'amenities',
  LOCATION_DETAILS: 'location_details',
  PRICING: 'pricing',
  SPECIFICATIONS: 'specifications',
  GALLERY: 'gallery',
  CONTACT_INFO: 'contact_info',
  CUSTOM: 'custom'
} as const;

// Document types enum
export const DOCUMENT_TYPES = {
  BROCHURE: 'brochure',
  FLOOR_PLAN: 'floor_plan',
  ADDITIONAL_INFO: 'additional_info',
  IMAGE: 'image',
  VIDEO: 'video',
  DOCUMENT: 'document'
} as const;

class ProjectService {
  // Project Metadata methods
  async getProjects(
    skip: number = 0,
    limit: number = 100,
    isActive?: boolean,
    searchQuery?: string
  ): Promise<{
    projects: ProjectMetadata[];
    total_count: number;
    skip: number;
    limit: number;
    has_more: boolean;
  }> {
    const params = new URLSearchParams();
    params.append('skip', skip.toString());
    params.append('limit', limit.toString());
    if (isActive !== undefined) params.append('is_active', isActive.toString());
    if (searchQuery) params.append('search_query', searchQuery);

    const response = await api.get(`/projects?${params.toString()}`);
    return response.data;
  }

  async getProject(projectCode: string): Promise<ProjectMetadata> {
    const response = await api.get(`/projects/${projectCode}`);
    return response.data;
  }

  async createProject(data: ProjectMetadataCreate): Promise<ProjectMetadata> {
    const response = await api.post('/projects', data);
    return response.data;
  }

  async updateProject(projectCode: string, data: ProjectMetadataUpdate): Promise<ProjectMetadata> {
    const response = await api.put(`/projects/${projectCode}`, data);
    return response.data;
  }

  async uploadProjectPdfLink(projectCode: string, file: File): Promise<ProjectPdfUploadResponse> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post(`/projects/${projectCode}/pdf-link/upload`, formData, {
      timeout: 300000,
    });
    return response.data;
  }

  async previewProjectAddress(projectCode: string, plusCode?: string): Promise<ProjectAddressPreview> {
    const response = await api.get(`/projects/${projectCode}/address-preview`, {
      params: plusCode ? { plus_code: plusCode } : undefined,
    });
    return response.data;
  }

  async previewProjectAddressByCoords(projectCode: string, lat: number, lng: number): Promise<ProjectAddressPreview> {
    const response = await api.get(`/projects/${projectCode}/address-preview`, {
      params: { lat, lng },
    });
    return response.data;
  }

  async deleteProject(projectCode: string): Promise<void> {
    await api.delete(`/projects/${projectCode}`);
  }

  // Project Context methods
  async getProjectContexts(
    filters: ProjectContextFilters = {},
    skip: number = 0,
    limit: number = 100
  ) {
    const params = new URLSearchParams();
    
    if (filters.project_code) params.append('project_code', filters.project_code);
    if (filters.context_type) params.append('context_type', filters.context_type);
    if (filters.is_active !== undefined) params.append('is_active', filters.is_active.toString());
    if (filters.created_by) params.append('created_by', filters.created_by);
    if (filters.search_query) params.append('search_query', filters.search_query);
    params.append('skip', skip.toString());
    params.append('limit', limit.toString());

    const response = await api.get(`/project-context/contexts?${params.toString()}`);
    return response.data;
  }

  async getProjectContext(contextId: number): Promise<ProjectContext> {
    const response = await api.get(`/project-context/contexts/${contextId}`);
    return response.data;
  }

  async createProjectContext(data: CreateProjectContextData): Promise<ProjectContext> {
    const response = await api.post('/project-context/contexts', data);
    return response.data;
  }

  async updateProjectContext(contextId: number, data: UpdateProjectContextData): Promise<ProjectContext> {
    const response = await api.put(`/project-context/contexts/${contextId}`, data);
    return response.data;
  }

  async deleteProjectContext(contextId: number): Promise<void> {
    await api.delete(`/project-context/contexts/${contextId}`);
  }

  // Project Document methods
  async getProjectDocuments(
    filters: ProjectDocumentFilters = {},
    skip: number = 0,
    limit: number = 100
  ) {
    const params = new URLSearchParams();
    
    if (filters.project_code) params.append('project_code', filters.project_code);
    if (filters.project_context_id) params.append('project_context_id', filters.project_context_id.toString());
    if (filters.document_type) params.append('document_type', filters.document_type);
    if (filters.is_active !== undefined) params.append('is_active', filters.is_active.toString());
    if (filters.uploaded_by) params.append('uploaded_by', filters.uploaded_by);
    params.append('skip', skip.toString());
    params.append('limit', limit.toString());

    const response = await api.get(`/project-context/documents?${params.toString()}`);
    return response.data;
  }

  async getProjectDocument(documentId: number): Promise<ProjectDocument> {
    const response = await api.get(`/project-context/documents/${documentId}`);
    return response.data;
  }

  async uploadProjectDocument(
    file: File,
    projectCode: string,
    documentType: string,
    uploadedBy: string,
    projectContextId?: number,
    description?: string,
    isActive: boolean = true
  ): Promise<ProjectDocument> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('project_code', projectCode);
    formData.append('document_type', documentType);
    formData.append('uploaded_by', uploadedBy);
    if (projectContextId !== undefined && projectContextId !== null) {
      formData.append('project_context_id', projectContextId.toString());
    }
    if (description) formData.append('description', description);
    formData.append('is_active', isActive.toString());

    const response = await api.post('/project-context/documents/upload', formData, {
      // Large uploads can legitimately take >10s; override axios default timeout from `api.ts`.
      timeout: 300000,
    });
    return response.data;
  }

  async updateProjectDocument(documentId: number, data: UpdateProjectDocumentData): Promise<ProjectDocument> {
    const response = await api.put(`/project-context/documents/${documentId}`, data);
    return response.data;
  }

  async deleteProjectDocument(documentId: number): Promise<void> {
    await api.delete(`/project-context/documents/${documentId}`);
  }

  async downloadProjectDocument(documentId: number): Promise<Blob> {
    const response = await api.get(`/project-context/documents/${documentId}/download`, {
      responseType: 'blob',
    });
    return response.data;
  }

  // Project Summary methods
  async getProjectSummaries(): Promise<ProjectSummary[]> {
    const response = await api.get('/project-context/summaries');
    return response.data;
  }

  async getProjectSummary(projectCode: string): Promise<ProjectSummary> {
    const response = await api.get(`/project-context/summaries/${projectCode}`);
    return response.data;
  }

  async getProjectsOverview(): Promise<{
    total_projects: number;
    active_projects: number;
    inactive_projects: number;
    total_contexts: number;
    total_documents: number;
    projects: ProjectSummary[];
    generated_at: string;
  }> {
    const response = await api.get('/projects/stats/overview');
    return response.data;
  }

  // Vector Store Sync methods
  async getSyncStatuses(projectCode?: string, skip: number = 0, limit: number = 100) {
    const params = new URLSearchParams();
    if (projectCode) params.append('project_code', projectCode);
    params.append('skip', skip.toString());
    params.append('limit', limit.toString());

    const response = await api.get(`/project-context/sync-status?${params.toString()}`);
    return response.data;
  }

  async getLatestSyncStatus(projectCode: string): Promise<VectorStoreSyncStatus> {
    const response = await api.get(`/project-context/sync-status/latest/${projectCode}`);
    return response.data;
  }

  async getSyncStatus(syncId: number): Promise<VectorStoreSyncStatus> {
    const response = await api.get(`/project-context/sync-status/${syncId}`);
    return response.data;
  }

  async syncProject(
    projectCode: string,
    syncType: 'full' | 'incremental' | 'document_only' = 'incremental',
    force: boolean = false,
    initiatedBy: string = 'system'
  ): Promise<VectorStoreSyncStatus> {
    const params = new URLSearchParams();
    params.append('sync_type', syncType);
    params.append('initiated_by', initiatedBy);
    if (force) params.append('force', 'true');

    const response = await api.post(`/project-context/sync/project/${projectCode}?${params.toString()}`);
    return response.data;
  }

  // Admin-triggered vector-store reindex (immediate)
  async syncProjectVectors(
    projectCode: string,
    syncType: 'full' | 'incremental' | 'document_only' = 'incremental',
    force: boolean = false
  ): Promise<VectorStoreSyncStatus> {
    const params = new URLSearchParams();
    params.append('sync_type', syncType);
    if (force) params.append('force', 'true');

    const response = await api.post(`/projects/${projectCode}/sync-vectors?${params.toString()}`);
    return response.data;
  }

  async refreshProjectAddress(projectCode: string): Promise<ProjectMetadata> {
    const response = await api.post(`/projects/${projectCode}/refresh-address`);
    return response.data;
  }

  async bulkSyncProjects(request: {
    project_codes: string[];
    sync_type: 'full' | 'incremental' | 'document_only';
    initiated_by: string;
  }): Promise<VectorStoreSyncStatus[]> {
    const response = await api.post('/project-context/sync/bulk', request);
    return response.data;
  }

  async cancelSync(syncId: number): Promise<{ message: string }> {
    const response = await api.delete(`/project-context/sync/${syncId}/cancel`);
    return response.data;
  }

  async getVectorStoreStats(): Promise<{
    total_contexts: number;
    total_documents: number;
    total_projects: number;
    projects: Record<string, { contexts: number; documents: number }>;
    last_updated: string;
  }> {
    const response = await api.get('/project-context/vector-store/stats');
    return response.data;
  }

  async cleanupVectorStore(projectCode?: string): Promise<{
    message: string;
    stats: {
      deleted_contexts: number;
      deleted_documents: number;
      total_deleted: number;
    };
  }> {
    const params = new URLSearchParams();
    if (projectCode) params.append('project_code', projectCode);

    const response = await api.delete(`/project-context/vector-store/cleanup?${params.toString()}`);
    return response.data;
  }

  // Utility methods
  async getContextTypes(): Promise<string[]> {
    const response = await api.get('/project-context/context-types');
    return response.data;
  }

  async getDocumentTypes(): Promise<string[]> {
    const response = await api.get('/project-context/document-types');
    return response.data;
  }

  // Helper method to get file extension from mime type
  getFileExtension(mimeType: string): string {
    const mimeToExt: { [key: string]: string } = {
      'application/pdf': 'pdf',
      'image/jpeg': 'jpg',
      'image/png': 'png',
      'image/gif': 'gif',
      'image/webp': 'webp',
      'video/mp4': 'mp4',
      'video/webm': 'webm',
      'application/msword': 'doc',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
      'application/vnd.ms-excel': 'xls',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
      'text/plain': 'txt',
    };
    return mimeToExt[mimeType] || 'file';
  }

  // Helper method to check if file is an image
  isImageFile(mimeType: string): boolean {
    return mimeType.startsWith('image/');
  }

  // Helper method to check if file is a PDF
  isPdfFile(mimeType: string): boolean {
    return mimeType === 'application/pdf';
  }

  // Helper method to format file size
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  // Inventory Sync methods
  async triggerInventorySync(projectCode?: string): Promise<{
    success: boolean;
    projects_processed: number;
    projects_succeeded: number;
    projects_failed: number;
    products_created: number;
    products_deleted: number;
    vectors_updated: number;
    errors?: string[];
  }> {
    const data = projectCode ? { project_code: projectCode } : undefined;
    const response = await api.post('/projects/sync-inventory', data);
    return response.data;
  }

  async getProjectProducts(
    projectCode: string,
    filters?: {
      availability_status?: string;
      product_type?: string;
      min_bedrooms?: number;
      max_price?: number;
    }
  ): Promise<{
    id: number;
    project_code: string;
    product_number: string;
    subproject_name?: string;
    typology_name?: string;
    product_type?: string;
    floor_level?: string;
    area?: number;
    num_bedrooms?: number;
    num_bathrooms?: string;
    orientation?: string;
    num_storage_rooms?: number;
    num_parking_spaces?: number;
    total_price?: number;
    availability_status: string;
    last_updated: string;
    created_at: string;
  }[]> {
    const params = new URLSearchParams();
    if (filters?.availability_status) params.append('availability_status', filters.availability_status);
    if (filters?.product_type) params.append('product_type', filters.product_type);
    if (filters?.min_bedrooms !== undefined) params.append('min_bedrooms', filters.min_bedrooms.toString());
    if (filters?.max_price !== undefined) params.append('max_price', filters.max_price.toString());

    const response = await api.get(`/projects/${projectCode}/products?${params.toString()}`);
    return response.data;
  }

  async getInventoryStatus(): Promise<{
    project_code: string;
    project_name: string;
    inventory_sync_status: string;
    last_inventory_sync?: string;
    total_units?: number;
    available_units?: number;
  }[]> {
    const response = await api.get('/projects/inventory-status');
    return response.data;
  }
}

export const projectService = new ProjectService();
export default projectService;
